/**
 * StepCardGroup — the numbered accordion the editors and the tutorial share:
 * one open step at a time, context-driven. Uncontrolled by default
 * (`defaultOpen`); pass `openIndex` + `onOpenChange` to drive it from the
 * outside (Back / Next / deep links). Plain state, not Radix — it only needs
 * summary-swap + non-toggling header zones the step pattern needs.
 * For Media Devoted | Created By Ciro Autiero
 */
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';

interface StepGroupContextValue {
  openIndex: number | null;
  toggle: (index: number) => void;
}

const StepGroupContext = createContext<StepGroupContextValue | null>(null);

export function useStepGroup(): StepGroupContextValue {
  const ctx = useContext(StepGroupContext);
  if (!ctx) throw new Error('StepCard must be rendered inside a StepCardGroup');
  return ctx;
}

interface Props {
  /** Step open on mount (1-based, matching StepCard index) — uncontrolled. */
  defaultOpen?: number;
  /** Controlled open step; pair with onOpenChange. */
  openIndex?: number | null;
  onOpenChange?: (index: number | null) => void;
  children: ReactNode;
}

export const StepCardGroup: FC<Props> = ({
  defaultOpen,
  openIndex,
  onOpenChange,
  children,
}) => {
  const [own, setOwn] = useState<number | null>(defaultOpen ?? null);
  const controlled = openIndex !== undefined;
  const current = controlled ? openIndex : own;
  const value = useMemo<StepGroupContextValue>(
    () => ({
      openIndex: current,
      toggle: (index: number) => {
        const next = current === index ? null : index;
        if (!controlled) setOwn(next);
        onOpenChange?.(next);
      },
    }),
    [current, controlled, onOpenChange],
  );
  return (
    <StepGroupContext.Provider value={value}>
      <div className="flex flex-col gap-2.5">{children}</div>
    </StepGroupContext.Provider>
  );
};
